import { Link } from 'react-router-dom'
import { APP_NAME, PLAN_LIMITS } from '@/lib/config'
import { useEffect } from 'react'

export default function ForWordpress() {
  useEffect(() => {
    document.title = `WordPress Analytics - ${APP_NAME} | No Plugin Required`
  }, [])

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo-icon.png" alt={APP_NAME} className="w-8 h-8" />
              <span className="text-xl font-bold text-gray-900">{APP_NAME}</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/login" className="text-sm font-medium text-gray-600 hover:text-gray-900">
                Sign in
              </Link>
              <Link to="/signup" className="btn-primary">
                Start free trial
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-800 px-4 py-1 rounded-full text-sm font-medium mb-6">
            <WordPressIcon className="w-4 h-4" />
            Works with WordPress
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            WordPress Analytics Without the Plugin Bloat
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Privacy-first analytics for your WordPress site.
            No plugin required - just one script tag in your header.
          </p>
          <Link to="/signup" className="btn-primary py-3 px-8 text-base">
            Start tracking free
          </Link>
          <p className="mt-4 text-sm text-gray-500">
            Works with any theme. No plugin installation needed.
          </p>
        </div>
      </section>

      {/* Why No Plugin */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
            Why Skip the Plugin?
          </h2>
          <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            Analytics plugins add database queries, admin overhead, and potential security risks.
            {APP_NAME} runs entirely in the browser.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit) => (
              <div key={benefit.title} className="card">
                <div className="text-3xl mb-4">{benefit.emoji}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Installation Methods */}
      <section className="py-16 bg-gray-50 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
            3 Ways to Install
          </h2>
          <p className="text-gray-600 text-center mb-12">
            Choose the method that works best for your WordPress setup.
          </p>

          {/* Method 1: Theme Editor */}
          <div className="card mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-700 font-semibold">1</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900">Theme Editor (Recommended)</h3>
            </div>
            <div className="space-y-3 ml-11">
              <p className="text-gray-600">1. Go to <strong>Appearance → Theme File Editor</strong></p>
              <p className="text-gray-600">2. Select your theme and open <strong>header.php</strong></p>
              <p className="text-gray-600">3. Find the <code className="bg-gray-100 px-1 rounded">&lt;/head&gt;</code> tag</p>
              <p className="text-gray-600">4. Paste the script just before it</p>
              <p className="text-gray-600">5. Click <strong>Update File</strong></p>
            </div>
          </div>

          {/* Method 2: Header Plugin */}
          <div className="card mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-700 font-semibold">2</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900">Header/Footer Plugin</h3>
            </div>
            <div className="space-y-3 ml-11">
              <p className="text-gray-600">If you already use a plugin like "Insert Headers and Footers" or "WPCode":</p>
              <p className="text-gray-600">1. Go to the plugin settings</p>
              <p className="text-gray-600">2. Paste the script in the <strong>Header</strong> section</p>
              <p className="text-gray-600">3. Save changes</p>
            </div>
          </div>

          {/* Method 3: Child Theme */}
          <div className="card mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-700 font-semibold">3</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900">Child Theme (Advanced)</h3>
            </div>
            <div className="space-y-3 ml-11">
              <p className="text-gray-600">For theme updates protection, add to your child theme's functions.php:</p>
              <div className="bg-gray-900 rounded-lg p-4 mt-3">
                <code className="text-green-400 text-sm whitespace-pre-wrap">
{`function add_userinsight_tracking() {
  echo '<script async src="https://userinsight.live/tracker.js" data-site-key="YOUR_KEY"></script>';
}
add_action('wp_head', 'add_userinsight_tracking');`}
                </code>
              </div>
            </div>
          </div>

          {/* Script Box */}
          <div className="bg-gray-900 rounded-lg p-6">
            <div className="flex justify-between items-center mb-2">
              <p className="text-gray-400 text-sm">Your tracking script</p>
              <span className="text-xs text-gray-500">Add to header.php or functions.php</span>
            </div>
            <code className="text-green-400 text-sm break-all">
              {`<script async src="https://userinsight.live/tracker.js" data-site-key="YOUR_KEY"></script>`}
            </code>
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
            vs WordPress Analytics Plugins
          </h2>
          <p className="text-gray-600 text-center mb-12">
            See how {APP_NAME} compares to popular WordPress analytics solutions.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-4 px-4 text-gray-600 font-medium">Feature</th>
                  <th className="text-center py-4 px-4 text-primary-600 font-semibold">{APP_NAME}</th>
                  <th className="text-center py-4 px-4 text-gray-600 font-medium">Jetpack Stats</th>
                  <th className="text-center py-4 px-4 text-gray-600 font-medium">MonsterInsights</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, i) => (
                  <tr key={i} className="border-b border-gray-100">
                    <td className="py-4 px-4 text-gray-900 font-medium">{row.feature}</td>
                    <td className="py-4 px-4 text-center">
                      {typeof row.userinsight === 'boolean' ? (
                        row.userinsight ? (
                          <CheckIcon className="w-5 h-5 text-green-500 mx-auto" />
                        ) : (
                          <XIcon className="w-5 h-5 text-red-500 mx-auto" />
                        )
                      ) : (
                        <span className="text-gray-900">{row.userinsight}</span>
                      )}
                    </td>
                    <td className="py-4 px-4 text-center">
                      {typeof row.jetpack === 'boolean' ? (
                        row.jetpack ? (
                          <CheckIcon className="w-5 h-5 text-green-500 mx-auto" />
                        ) : (
                          <XIcon className="w-5 h-5 text-red-500 mx-auto" />
                        )
                      ) : (
                        <span className="text-gray-500">{row.jetpack}</span>
                      )}
                    </td>
                    <td className="py-4 px-4 text-center">
                      {typeof row.monster === 'boolean' ? (
                        row.monster ? (
                          <CheckIcon className="w-5 h-5 text-green-500 mx-auto" />
                        ) : (
                          <XIcon className="w-5 h-5 text-red-500 mx-auto" />
                        )
                      ) : (
                        <span className="text-gray-500">{row.monster}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-gray-50 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Frequently Asked Questions
          </h2>
          <div className="space-y-8">
            {faqs.map((faq, i) => (
              <div key={i} className="border-b border-gray-200 pb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{faq.question}</h3>
                <p className="text-gray-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-blue-600 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to track your WordPress site?
          </h2>
          <p className="text-blue-100 mb-8">
            Start free with {PLAN_LIMITS.sessionsPerMonth.toLocaleString()} sessions/month. No credit card required.
          </p>
          <Link
            to="/signup"
            className="inline-flex items-center justify-center px-8 py-3 text-base font-medium rounded-lg bg-white text-blue-600 hover:bg-blue-50 transition-colors"
          >
            Start free trial
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-gray-200 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} {APP_NAME}. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link to="/" className="text-gray-500 hover:text-gray-700 text-sm">Home</Link>
              <Link to="/vs-google-analytics" className="text-gray-500 hover:text-gray-700 text-sm">vs Google Analytics</Link>
              <Link to="/for-shopify" className="text-gray-500 hover:text-gray-700 text-sm">Shopify Analytics</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

function WordPressIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.486 2 2 6.486 2 12s4.486 10 10 10 10-4.486 10-10S17.514 2 12 2zM3.443 12c0-1.096.21-2.143.592-3.105l3.26 8.934A8.568 8.568 0 013.443 12zm8.557 8.557c-.882 0-1.733-.141-2.53-.401l2.688-7.807 2.753 7.544c.018.044.039.085.063.125a8.522 8.522 0 01-2.974.539zm1.22-12.563c.538-.028 1.024-.084 1.024-.084.482-.057.425-.766-.057-.74 0 0-1.451.114-2.388.114-.882 0-2.364-.114-2.364-.114-.483-.026-.538.712-.057.74 0 0 .458.056.94.084l1.396 3.826-1.96 5.878-3.261-9.704c.538-.028 1.024-.084 1.024-.084.483-.057.425-.766-.056-.74 0 0-1.452.114-2.388.114-.168 0-.366-.004-.578-.01A8.537 8.537 0 0112 3.443c2.209 0 4.228.846 5.74 2.232-.037-.002-.072-.007-.11-.007-.882 0-1.508.769-1.508 1.594 0 .74.427 1.367.882 2.108.342.596.74 1.363.74 2.47 0 .767-.295 1.656-.683 2.897l-.896 2.99-3.245-9.655zm4.396 12.152l2.733-7.9c.511-1.277.683-2.299.683-3.209 0-.33-.022-.637-.062-.923a8.54 8.54 0 011.03 4.086c0 3.172-1.721 5.942-4.284 7.436z"/>
    </svg>
  )
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  )
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  )
}

const benefits = [
  {
    emoji: '🚀',
    title: 'Faster Site',
    description: 'No plugin means no extra PHP execution, no database queries, no admin bloat. Just a lightweight script.',
  },
  {
    emoji: '🔒',
    title: 'More Secure',
    description: 'Fewer plugins = smaller attack surface. No WordPress-side code to maintain or update.',
  },
  {
    emoji: '⚡',
    title: 'Simple Updates',
    description: 'Theme updates won\'t break your analytics. No plugin compatibility issues to worry about.',
  },
  {
    emoji: '📊',
    title: 'Session Replay',
    description: 'Watch how visitors interact with your content. See what they read, where they click, how far they scroll.',
  },
  {
    emoji: '🔐',
    title: 'Privacy-First',
    description: 'First-party cookies only. No personal data. GDPR compliant without consent banners in most cases.',
  },
  {
    emoji: '💰',
    title: 'No Premium Upsells',
    description: 'All features included in our free tier. No "upgrade to see more" prompts in your dashboard.',
  },
]

const comparisonData = [
  { feature: 'Requires plugin', userinsight: false, jetpack: true, monster: true },
  { feature: 'Session replay', userinsight: true, jetpack: false, monster: false },
  { feature: 'Privacy-first', userinsight: true, jetpack: true, monster: false },
  { feature: 'No consent banner needed', userinsight: true, jetpack: true, monster: false },
  { feature: 'Real-time data', userinsight: true, jetpack: false, monster: true },
  { feature: 'Database queries', userinsight: 'None', jetpack: 'Many', monster: 'Many' },
  { feature: 'Free tier', userinsight: '5K sessions', jetpack: 'Limited', monster: 'Very limited' },
  { feature: 'Admin overhead', userinsight: 'None', jetpack: 'Medium', monster: 'High' },
  { feature: 'Script size', userinsight: '<10KB', jetpack: 'Variable', monster: '45KB+ (GA)' },
]

const faqs = [
  {
    question: 'Do I really not need a plugin?',
    answer: 'Correct! UserInsight.live runs entirely from a script tag in your site\'s header. No WordPress plugin, no database tables, no admin pages.',
  },
  {
    question: 'What if I update my theme?',
    answer: 'If you\'re using a child theme or a header injection plugin, your script will persist. If you edited header.php directly, you\'ll need to re-add the script after theme updates.',
  },
  {
    question: 'Does it work with caching plugins?',
    answer: 'Yes. Our script loads asynchronously and works with all caching plugins including WP Super Cache, W3 Total Cache, and WP Rocket.',
  },
  {
    question: 'Will it track logged-in users?',
    answer: 'Yes. UserInsight.live tracks all visitors including logged-in administrators. You can identify admin traffic by viewing from your own browser.',
  },
  {
    question: 'Does it work with WooCommerce?',
    answer: 'Yes! For WooCommerce stores, UserInsight.live auto-detects the platform and tracks the entire customer journey through your shop.',
  },
  {
    question: 'Is it GDPR compliant?',
    answer: 'Yes. UserInsight.live uses first-party cookies only and collects no personal data. In most jurisdictions, you don\'t need a cookie consent banner.',
  },
]
